import { themeGet } from '@styled-system/theme-get'
import React from 'react'
import styled from 'styled-components'
import {
  ProgressBarCar, ProgressBarFinishFlag, ProgressBarFinishPoleLeft, ProgressBarFinishPoleRight
} from '@zuto/icons'
import { Theme, useTheme } from '@mui/material'

export interface ProgressBarProps extends React.HTMLAttributes<HTMLDivElement> {
  percentage?: number
  decimal?: number
  showCar?: boolean
  amountOfQuestions?: number
  currentQuestionIndex?: number
}

type BarProps = {
  showCar: boolean
  muiTheme: Theme 
}

type ProgressProps = {
  progress: number
  muiTheme: Theme 
  showCar: boolean
}

const Bar = styled.div<BarProps>(({ muiTheme, showCar }) => {
  const baseStyles = muiTheme.customComponents.progressBar.bar
  return {
    ...baseStyles,
    overflow: showCar ? 'unset' : undefined,
    position: showCar ? 'relative' : undefined
  }
})

const Progress = styled.div<ProgressProps>(({ muiTheme, progress, showCar }) => {
  const baseStyles = muiTheme.customComponents.progressBar.progress
  return {
    ...baseStyles,
    width: showCar ? `${progress -2}%` : `${progress}%`
  }
})

const CarWrapper = styled.div<{ progress: number }>`
${themeGet('progressBar.carWrapper')}
  transform: translateX(${(props) => props.progress}%);
  transition: transform 0.6s ease-out;
`

const FlagWrapper = styled.div`
${themeGet('progressBar.flagWrapper')}
`
const FinalFlagLeftWrapper = styled.div`
${themeGet('progressBar.finalFlagLeftWrapper')}
`

const FinalFlagRightWrapper = styled.div`
${themeGet('progressBar.finalFlagRightWrapper')}
`

const ProgressBar = ({
  percentage,
  decimal,
  showCar = false,
  amountOfQuestions,
  currentQuestionIndex,
  ...props
}: ProgressBarProps) => {
  const muiTheme = useTheme()
  
  const width = window.innerWidth
  let offset

  switch (true) {
    case (width <= 340):
      offset = 13
      break
    case (width <= 380):
      offset = 11
      break
    case (width <= 500):
      offset = 9
      break
    case (width <= 640):
      offset = 7
      break
    case (width <= 797):
      offset = 6
      break
    default:
      offset = 6.8
  }

  const calculateComponentPercentage = (actualPercentage?: number, actualDecimal?: number, actualoffset = 0, adjustCarPosition = false) => {
    if (actualPercentage !== undefined) {
      const safePercentage = Number.isFinite(actualPercentage) ? actualPercentage : 0.0
      // With no offset to compensate for, a negative input is treated as its magnitude.
      // With an offset (positioning the car so it doesn't overflow the bar), the result
      // must be clamped at 0 rather than reflected, otherwise the car appears to move
      // backwards whenever the raw value is smaller than the offset.
      return actualoffset > 0 ? Math.max(0, safePercentage - actualoffset) : Math.abs(safePercentage)
    }

    if (actualDecimal !== undefined) {
      const safeDecimal = Number.isFinite(actualDecimal) ? actualDecimal : 0.0
      const newDecimal = actualoffset > 0 ? Math.max(0, safeDecimal - (actualoffset / 100)) : Math.abs(safeDecimal)

      // We need to add additional negative offset to the car on the last question
      // to prevent it from going off the screen whilst keeping the bar at 100% width
      if (adjustCarPosition && currentQuestionIndex === amountOfQuestions) {
        return (newDecimal * 100) - 3
      }
      return newDecimal * 100
    }

    return 0
  }

  const progressBarPercentage = calculateComponentPercentage(percentage, decimal, 0)
  const carPercentage : number = calculateComponentPercentage(percentage, decimal, offset, true)

  return (
    <Bar {...props} muiTheme={muiTheme} showCar={showCar} className="progress-bar">
      <Progress muiTheme={muiTheme} showCar={showCar} className="progress-bar-percentage" progress={progressBarPercentage} />
      {showCar
        && (
          <>
            <CarWrapper progress={carPercentage} data-testid="progress-bar-car">
              <ProgressBarCar />
            </CarWrapper>
            <FlagWrapper data-testid="progress-bar-finish-flag">
              <FinalFlagLeftWrapper>
                <ProgressBarFinishPoleLeft />
                <ProgressBarFinishFlag />
              </FinalFlagLeftWrapper>
              <FinalFlagRightWrapper>
                <ProgressBarFinishPoleRight />
              </FinalFlagRightWrapper>
            </FlagWrapper>
          </>
        )}
    </Bar>
  )
}

export default ProgressBar
